// Mock data for ZEEVA Life - Human Capability & Longevity Platform

export const mockData = {
  brand: {
    name: "ZEEVA Life",
    sanskritMotto: "बलं स्वास्थ्यं आयुः",
    mottoTranslation: "Strength • Health • Longevity",
    tagline: "Build Strength. Preserve Mobility. Extend Vitality.",
    description: "Evidence-based exercise, rehabilitation, mobility and lifestyle strategies designed to help individuals remain strong, independent and resilient throughout life.",
    positioning: "Helping people build strength, mobility, resilience and lifelong capability.",
    secondaryPositioning: "From childhood development to active aging, Zeeva helps individuals thrive through evidence-based movement, exercise, rehabilitation and health education."
  },

  mission: "To empower individuals of all ages to build strength, improve health, develop resilience and maintain lifelong physical capability through evidence-based movement, exercise, rehabilitation and lifestyle interventions.",

  vision: "A world where individuals of all ages develop the strength, mobility, resilience and health needed to thrive throughout life.",

  hero: {
    headline: "Build Strength.\nPreserve Mobility.\nExtend Vitality.",
    subheading: "Evidence-based exercise, rehabilitation, mobility and lifestyle strategies designed to help individuals remain strong, independent and resilient throughout life.",
    primaryCTA: "Start Your Assessment",
    primaryCTALink: "/assessment",
    secondaryCTA: "Explore Programs",
    secondaryCTALink: "#programs"
  },

  modernChallenge: {
    title: "The Modern Health Challenge",
    description: "Today's lifestyle presents unprecedented challenges to human capability and healthspan.",
    challenges: [
      {
        icon: "Armchair",
        title: "Sedentary Living",
        description: "Prolonged sitting and reduced physical activity leading to declining strength and mobility"
      },
      {
        icon: "AlertCircle",
        title: "Poor Posture",
        description: "Desk work and device use creating chronic pain and movement dysfunction"
      },
      {
        icon: "Brain",
        title: "Chronic Stress",
        description: "Persistent mental and physical stress affecting recovery and health"
      },
      {
        icon: "TrendingDown",
        title: "Declining Mobility",
        description: "Loss of movement quality and range affecting independence"
      },
      {
        icon: "Activity",
        title: "Physical Inactivity",
        description: "Insufficient exercise contributing to lifestyle diseases"
      },
      {
        icon: "Clock",
        title: "Early Disease Onset",
        description: "Metabolic and cardiovascular conditions appearing earlier in life"
      }
    ],
    solution: "Zeeva provides evidence-based solutions to build strength, restore mobility, reduce stress and maintain lifelong capability."
  },

  framework: {
    title: "The Zeeva Framework",
    subtitle: "Five pillars of lifelong capability",
    pillars: [
      {
        id: 1,
        title: "MOVE",
        subtitle: "Movement Quality & Function",
        description: "Develop movement quality, mobility and functional capacity for daily life.",
        icon: "Activity",
        color: "from-brand-primary to-brand-secondary"
      },
      {
        id: 2,
        title: "GROW",
        subtitle: "Strength & Development",
        description: "Build strength, develop physical capacity and enhance athletic performance.",
        icon: "TrendingUp",
        color: "from-brand-secondary to-brand-accent"
      },
      {
        id: 3,
        title: "RECOVER",
        subtitle: "Recovery & Adaptation",
        description: "Optimize recovery, adapt effectively and maintain sustainable health practices.",
        icon: "Heart",
        color: "from-brand-accent to-brand-primary"
      },
      {
        id: 4,
        title: "THRIVE",
        subtitle: "Performance & Vitality",
        description: "Enhance performance, increase vitality and improve overall well-being.",
        icon: "Zap",
        color: "from-brand-primary to-brand-accent"
      },
      {
        id: 5,
        title: "ENDURE",
        subtitle: "Longevity & Independence",
        description: "Maintain strength, preserve independence and support healthy aging.",
        icon: "Target",
        color: "from-brand-secondary to-brand-primary"
      }
    ]
  },

  audiences: [
    {
      id: 1,
      title: "Children & Youth Development",
      ageRange: "5-18 years",
      description: "Building fundamental movement skills, athletic development and lifelong healthy habits.",
      icon: "Users",
      outcomes: ["Movement competency", "Athletic development", "Injury prevention", "Confidence building"]
    },
    {
      id: 2,
      title: "Adults Seeking Better Health",
      ageRange: "19-55 years",
      description: "Improving strength, mobility, metabolic health and stress resilience.",
      icon: "Activity",
      outcomes: ["Strength development", "Metabolic health", "Stress management", "Quality of life"]
    },
    {
      id: 3,
      title: "Professionals & Executives",
      ageRange: "25-60 years",
      description: "Managing sedentary work, preventing burnout and maintaining performance.",
      icon: "Briefcase",
      outcomes: ["Posture correction", "Stress reduction", "Energy optimization", "Performance"]
    },
    {
      id: 4,
      title: "Rehabilitation & Recovery",
      ageRange: "All ages",
      description: "Recovering from injury, surgery or chronic conditions with evidence-based protocols.",
      icon: "Heart",
      outcomes: ["Pain reduction", "Function restoration", "Strength rebuilding", "Independence"]
    },
    {
      id: 5,
      title: "Special Populations",
      ageRange: "All ages",
      description: "Adapted exercise for chronic conditions, disabilities and unique needs.",
      icon: "Shield",
      outcomes: ["Adapted movement", "Condition management", "Quality of life", "Capability"]
    },
    {
      id: 6,
      title: "Active Aging Adults",
      ageRange: "55+ years",
      description: "Maintaining strength, mobility, independence and quality of life.",
      icon: "Target",
      outcomes: ["Fall prevention", "Strength maintenance", "Mobility", "Independence"]
    },
    {
      id: 7,
      title: "Performance & Fitness Seekers",
      ageRange: "All ages",
      description: "Enhancing athletic performance, building strength and achieving fitness goals.",
      icon: "Zap",
      outcomes: ["Performance gains", "Strength building", "Athletic development", "Goal achievement"]
    }
  ],

  programs: [
    {
      id: 1,
      slug: "capability-assessment",
      name: "ZEEVA Capability Assessment™",
      duration: "Single session + report",
      price: "Starting From ₹2,999",
      image: "/images/programs/active-aging.jpg",
      category: "Assessment",
      cta: "Start Your Assessment™",
      ctaTarget: "/assessment",
      ideal: "For individuals beginning their journey toward lifelong capability — the entry point into every Zeeva program pathway.",
      description: "A comprehensive evaluation across the six domains of human capability. Establishes your baseline, identifies your gaps and produces a personalised recommendation for the right Zeeva program pathway.",
      outcomes: [
        "Health & Lifestyle Assessment",
        "Movement & Mobility Review",
        "Goal Analysis",
        "Blood Work Review (Optional)",
        "Health Records Review (Optional)",
        "Zeeva Capability Score",
        "Personalized Consultation",
        "Live Sessions with Coach"
      ],
      features: [
        "Health & Lifestyle Assessment",
        "Movement & Mobility Review",
        "Goal Analysis",
        "Blood Work Review (Optional)",
        "Health Records Review (Optional)",
        "Zeeva Capability Score",
        "Personalized Consultation",
        "Live Sessions with Coach"
      ]
    },
    {
      id: 2,
      slug: "human-reset",
      name: "ZEEVA Human Reset™",
      duration: "12 Weeks",
      price: "Starting From ₹24,999",
      image: "https://images.unsplash.com/photo-1765045847158-11e23e972bb0",
      category: "Reset",
      cta: "Apply After Assessment",
      ctaTarget: "/assessment",
      ideal: "Designed for individuals seeking foundational change — fat loss, strength development, metabolic health and a lifestyle reset.",
      description: "A structured 12-week pathway combining personalised training, nutrition guidance and weekly accountability to rebuild the foundations of capability and vitality.",
      outcomes: [
        "Fat Loss",
        "Strength Development",
        "Metabolic Health",
        "Lifestyle Reset",
        "Habit Building",
        "Energy Improvement"
      ],
      features: [
        "Personalized Training",
        "Live Sessions with Coach",
        "Nutrition Guidance",
        "Progress Tracking",
        "Weekly Accountability"
      ]
    },
    {
      id: 3,
      slug: "reset-premium",
      name: "ZEEVA Reset Premium™",
      duration: "12 Weeks",
      price: "Starting From ₹39,999",
      image: "https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6",
      category: "Premium Reset",
      cta: "Apply After Assessment",
      ctaTarget: "/assessment",
      ideal: "For individuals who want the Human Reset experience with deeper one-on-one support and constant coach access.",
      description: "Everything in ZEEVA Human Reset™ — elevated with weekly 1-on-1 live coaching, technique reviews and ongoing strategy adjustments.",
      outcomes: [
        "Everything in Human Reset",
        "Weekly Live 1-on-1 Coaching Session",
        "Exercise Technique Review",
        "24/7 Coach Access",
        "Monthly Strategy Reviews"
      ],
      features: [
        "Everything in Human Reset",
        "Weekly Live 1-on-1 Coaching Session",
        "Exercise Technique Review",
        "24/7 Coach Access",
        "Monthly Strategy Reviews"
      ]
    },
    {
      id: 4,
      slug: "evolve",
      name: "ZEEVA Evolve™",
      duration: "24 Weeks",
      price: "Starting From ₹69,999",
      image: "/images/programs/strength-capability.jpg",
      category: "Evolution",
      cta: "Apply After Assessment",
      ctaTarget: "/assessment",
      ideal: "Designed for sustained transformation — long-term body composition, strength, mobility and functional performance for healthy aging.",
      description: "A 24-week pathway built around progressive coaching, training evolution and lifestyle optimisation. The pathway to long-term capability that compounds for life.",
      outcomes: [
        "Long-Term Body Composition",
        "Strength & Mobility",
        "Healthy Aging",
        "Functional Performance",
        "Sustainable Health Improvements"
      ],
      features: [
        "Progressive Coaching",
        "Training Evolution",
        "Lifestyle Optimization",
        "Regular Reviews",
        "Live Sessions with Coach"
      ]
    },
    {
      id: 5,
      slug: "elite",
      name: "ZEEVA Elite™",
      duration: "Monthly Coaching",
      price: "Starting From ₹18,000/month",
      image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438",
      category: "Elite",
      cta: "Schedule Consultation",
      ctaTarget: "/assessment",
      ideal: "Designed for executives, entrepreneurs and high performers who require long-term accountability and priority support.",
      description: "Monthly coaching with priority access, ongoing optimisation and a coach who understands the demands of leadership and performance.",
      outcomes: [
        "Executives",
        "Entrepreneurs",
        "High-Performers",
        "Long-Term Accountability"
      ],
      features: [
        "Personalized Coaching",
        "Live Sessions",
        "Priority Support",
        "Ongoing Optimization"
      ]
    },
    {
      id: 6,
      slug: "longevity-partnership",
      name: "ZEEVA Longevity Partnership™",
      duration: "Annual Program",
      price: "Custom Investment",
      image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f",
      category: "Longevity",
      cta: "Book Consultation",
      ctaTarget: "/assessment",
      ideal: "Designed for individuals committed to comprehensive lifelong capability — healthspan optimisation, executive health and active aging.",
      description: "An annual partnership that integrates healthspan optimisation, executive health, active aging, performance, recovery and comprehensive lifestyle management.",
      outcomes: [
        "Healthspan Optimization",
        "Executive Health",
        "Active Aging",
        "Performance & Recovery",
        "Comprehensive Lifestyle Management"
      ],
      features: [
        "Annual Partnership",
        "Healthspan Optimization",
        "Executive Health",
        "Performance & Recovery",
        "Comprehensive Lifestyle Management"
      ]
    }
  ],

  whyZeeva: {
    title: "Why Zeeva",
    headline: "The Science of Staying Capable",
    philosophy: "Health is not merely the absence of disease. Health is the ability to move freely, think clearly, adapt effectively, recover consistently and remain capable throughout life.",
    principles: [
      {
        icon: "Activity",
        title: "Exercise Therapy",
        description: "Evidence-based exercise as medicine for prevention and treatment"
      },
      {
        icon: "Target",
        title: "Functional Training",
        description: "Movement patterns and capabilities that transfer to real life"
      },
      {
        icon: "TrendingUp",
        title: "Strength Development",
        description: "Progressive resistance training for lifelong capability"
      },
      {
        icon: "Activity",
        title: "Mobility Restoration",
        description: "Systematic approaches to improving movement quality and function"
      },
      {
        icon: "Heart",
        title: "Healthy Aging",
        description: "Strategies to maintain independence and quality of life with age"
      },
      {
        icon: "BookOpen",
        title: "Evidence-Based Practice",
        description: "Interventions grounded in scientific research and clinical experience"
      }
    ]
  },

  progressStories: [
    {
      id: 1,
      clientName: "Rajesh M.",
      age: 58,
      program: "Active Aging & Independence",
      outcome: "Regained Mobility & Confidence",
      story: "After years of knee pain, I can now walk without discomfort and play with my grandchildren. My strength and balance have improved dramatically.",
      metrics: ["50% pain reduction", "Improved balance", "Increased daily activity", "Better quality of life"],
      image: "/images/transformations/couple-gym.webp"
    },
    {
      id: 2,
      clientName: "Priya S.",
      age: 34,
      program: "Metabolic Health & Body Recomposition",
      outcome: "Improved Health & Energy",
      story: "My metabolic markers improved significantly, energy levels are higher, and I feel stronger than ever. The sustainable approach made all the difference.",
      metrics: ["Improved metabolic health", "Increased strength", "Better sleep", "More energy"],
      image: "/images/transformations/female-transformation.jpg"
    },
    {
      id: 3,
      clientName: "Arjun K.",
      age: 16,
      program: "Youth Athletic Development",
      outcome: "Enhanced Performance",
      story: "My speed and strength improved, and I made the school cricket team. The training helped me prevent injuries and perform better.",
      metrics: ["Strength gains", "Better movement", "Injury prevention", "Performance gains"],
      image: "/images/transformations/male-transformation.jpg"
    },
    {
      id: 4,
      clientName: "Meera P.",
      age: 45,
      program: "Mobility Restoration",
      outcome: "Pain-Free Movement",
      story: "Years of back pain are gone. I can move freely, exercise confidently and enjoy activities I thought were impossible.",
      metrics: ["Pain elimination", "Movement restoration", "Increased confidence", "Better function"],
      image: "/images/transformations/woman-pink-pose.jpg"
    },
    {
      id: 5,
      clientName: "Vikram D.",
      age: 52,
      program: "Recovery & Resilience",
      outcome: "Post-Surgery Recovery",
      story: "After shoulder surgery, I regained full function and strength. The rehabilitation program was professional and effective.",
      metrics: ["Full recovery", "Strength restored", "Confidence rebuilt", "Return to activities"],
      image: "/images/transformations/yoga-bridge.jpg"
    }
  ],

  contact: {
    email: "healwith@zeevalife.com",
    phone: "+91 97309 84048",
    phone2: "+91 99233 12502",
    whatsapp: "+919730984048",
    address: "Mumbai, Maharashtra, India",
    socialMedia: {
      instagram: "https://instagram.com/zeeva_living",
      facebook: "",
      youtube: ""
    }
  },

  cta: {
    title: "Begin Your Path to Lifelong Capability",
    subtitle: "Take the first step toward strength, mobility and lasting vitality. Start with a complimentary capability assessment.",
    primaryAction: "Start Your Assessment",
    primaryActionLink: "/assessment",
    secondaryAction: "Explore Programs",
    secondaryActionLink: "#programs"
  },

  blogPosts: [
    {
      id: 1,
      slug: "strength-training-longevity",
      title: "Strength Training: The Single Most Important Habit for Longevity",
      excerpt: "Why progressive resistance training is the most evidence-backed intervention for healthspan, mobility and independence as you age.",
      author: "ZEEVA Life Team",
      date: "2026-01-18",
      category: "Longevity",
      image: "https://images.unsplash.com/photo-1648542813876-bdf57e0cfd0b?w=1200&q=80",
      readTime: "7 min read"
    },
    {
      id: 2,
      slug: "mobility-versus-flexibility",
      title: "Mobility vs Flexibility: What Actually Determines How You Move",
      excerpt: "Understanding the difference between mobility and flexibility – and why training mobility properly transforms how you feel every day.",
      author: "ZEEVA Life Team",
      date: "2026-01-10",
      category: "Mobility",
      image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80",
      readTime: "6 min read"
    },
    {
      id: 3,
      slug: "active-aging-independence",
      title: "Active Aging: Building Strength to Stay Independent for Life",
      excerpt: "Practical, evidence-based protocols for maintaining strength, balance and mobility through every decade of life.",
      author: "ZEEVA Life Team",
      date: "2026-01-03",
      category: "Active Aging",
      image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80",
      readTime: "8 min read"
    },
    {
      id: 4,
      slug: "recovery-and-resilience",
      title: "Recovery is Where Capability is Built",
      excerpt: "How structured recovery, sleep and stress management compound into lifelong resilience and performance.",
      author: "ZEEVA Life Team",
      date: "2025-12-22",
      category: "Recovery",
      image: "https://images.unsplash.com/photo-1598901986949-f593ff2a31a6?w=1200&q=80",
      readTime: "5 min read"
    },
    {
      id: 5,
      slug: "rehabilitation-evidence-based",
      title: "Evidence-Based Rehabilitation: A Modern Approach to Recovery",
      excerpt: "Why progressive loading, exercise therapy and movement assessment deliver durable results in injury and post-surgical recovery.",
      author: "ZEEVA Life Team",
      date: "2025-12-14",
      category: "Rehabilitation",
      image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1200&q=80",
      readTime: "7 min read"
    },
    {
      id: 6,
      slug: "youth-athletic-development",
      title: "Long-Term Athletic Development for Young Athletes",
      excerpt: "Building the movement competency, strength foundation and resilience that determine athletic outcomes.",
      author: "ZEEVA Life Team",
      date: "2025-12-05",
      category: "Youth Development",
      image: "https://images.unsplash.com/photo-1607962837359-5e7e89f86776?w=1200&q=80",
      readTime: "6 min read"
    }
  ]
};

// Backwards-compat aliases for existing pages that read these names
mockData.services = mockData.audiences;
mockData.testimonials = mockData.progressStories;
mockData.whyChoose = mockData.whyZeeva.principles;

// Helper function to generate WhatsApp link
export const generateWhatsAppLink = (message = "Hi, I'm interested in learning more about ZEEVA Life programs") => {
  const phone = mockData.contact.whatsapp.replace(/\D/g, '');
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${phone}?text=${encodedMessage}`;
};

// Contact form submission handler (local persistence; backend wiring is Phase 2)
export const handleContactFormSubmit = (formData) => {
  try {
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('contact submission storage failed:', err);
    }
  }
  return { success: true, message: "Thank you. Our team will reach out within 24 hours." };
};

// Newsletter signup handler (local persistence; backend wiring is Phase 2)
export const handleNewsletterSignup = (email) => {
  try {
    const subscribers = JSON.parse(localStorage.getItem('newsletterSubscribers') || '[]');
    subscribers.push({ email, timestamp: new Date().toISOString() });
    localStorage.setItem('newsletterSubscribers', JSON.stringify(subscribers));
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('newsletter storage failed:', err);
    }
  }
  return { success: true, message: "You're subscribed. Welcome to the ZEEVA Life community." };
};
